/* =====================================================
   Damlab — Cart Drawer
   ----------------------------------------------------
   ARCHITECTURE
   ----------------------------------------------------
   Stateless subscriber UI. Owns ZERO commerce state.

   - Renders snapshots from `cart:updated` bus events
   - Mutations dispatch through `App.cart.change/update/clear`
   - Auto-opens on `cart:item-added` (post-add UX)
   - Re-renders ONLY when cart payload changes
   - Optimistic line-removal: marks `is-pending`, lets the
     next `cart:updated` reconcile

   Future-ready slots (filled by content, not redesign):
     - Upsells / post-add recommendations  (data-cart-upsell)
     - Free-shipping threshold bar          (data-cart-shipping)
     - Discount visualization                (data-cart-discounts)
     - Selling-plan / subscription label     (data-line-selling-plan)
     - Bundle-line indicator                 (.cart-drawer__line--bundle CSS hook)

   Bus contract:
     IN:  cart:updated, cart:item-added
     OUT: drawer:opened, drawer:closed,
          free-shipping:reached, free-shipping:not-reached
   ===================================================== */
(function (App) {
  'use strict';
  if (!App) { console.error('[cart-drawer] App core not loaded'); return; }

  const MONEY_FMT = new Intl.NumberFormat('nl-NL', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2
  });
  const formatMoney = (cents, currency) => {
    if (cents == null) return '';
    if (currency && currency !== 'EUR') {
      return new Intl.NumberFormat('nl-NL', { style: 'currency', currency }).format(cents / 100);
    }
    return MONEY_FMT.format(cents / 100);
  };

  App.register('cart.drawer', {
    mount(ctx) {
      const drawer       = document.querySelector('[data-cart-drawer]');
      if (!drawer) return;

      const overlay      = drawer.querySelector('[data-cart-overlay]');
      const panel        = drawer.querySelector('.cart-drawer__panel');
      const closeBtn     = drawer.querySelector('[data-cart-close]');
      const itemsEl      = drawer.querySelector('[data-cart-items]');
      const emptyEl      = drawer.querySelector('[data-cart-empty]');
      const footerEl     = drawer.querySelector('[data-cart-footer]');
      const subtotalEl   = drawer.querySelector('[data-cart-subtotal]');
      const countTextEl  = drawer.querySelector('[data-cart-count-text]');
      const checkoutBtn  = drawer.querySelector('[data-cart-checkout]');
      const shippingEl   = drawer.querySelector('[data-cart-shipping]');
      const shipTextEl   = drawer.querySelector('[data-cart-shipping-text]');
      const shipFillEl   = drawer.querySelector('[data-cart-shipping-fill]');
      const discountsEl  = drawer.querySelector('[data-cart-discounts]');
      const lineTpl      = drawer.querySelector('[data-cart-line-tpl]')?.content?.firstElementChild;
      const upsellEl     = drawer.querySelector('[data-cart-upsell]');
      const upsellRail   = drawer.querySelector('[data-cart-upsell-rail]');
      const upsellCount  = drawer.querySelector('[data-cart-upsell-count]');
      const upsellCardTpl= drawer.querySelector('[data-cart-upsell-card-tpl]')?.content?.firstElementChild;

      // ── Upsell config (settings → shopify-data.liquid → App.data) ──
      const MIN_QTY = 1, MAX_QTY = 10;
      const recsUrl    = App.data?.routes?.product_recs || '';
      const cartCfg    = () => App.data?.cart_settings || {};
      // Smart recos need the recommendations route (live storefront only) AND
      // the merchant toggle (default ON when a route exists).
      const smartOn    = () => !!recsUrl && cartCfg().upsell_smart_enabled !== false;
      const railLimit  = () => { const n = parseInt(cartCfg().upsell_rail_limit, 10); return n > 0 ? n : 4; };

      // ── Open / close (UI state — NOT cart state) ─────
      let isOpen = false;
      let lastFocused = null;

      function setOpen(open) {
        if (open === isOpen) return;
        isOpen = open;
        drawer.classList.toggle('is-open', open);
        drawer.setAttribute('aria-hidden', String(!open));
        document.body.style.overflow = open ? 'hidden' : '';
        if (open) {
          lastFocused = document.activeElement;
          // focus the close button for keyboard users
          requestAnimationFrame(() => closeBtn?.focus());
        } else if (lastFocused?.focus) {
          lastFocused.focus();
        }
        App.bus.emit(open ? 'drawer:opened' : 'drawer:closed', { drawer: 'cart' });
      }

      // ── Render from cart snapshot ────────────────────
      let lastRenderedKey = null; // dirty-check on cart shape change

      function renderLine(item) {
        if (!lineTpl) return null;
        const node = lineTpl.cloneNode(true);
        node.dataset.lineKey   = item.key || '';
        node.dataset.productId = String(item.product_id || '');
        node.dataset.variantId = String(item.variant_id || item.id || '');

        const img = node.querySelector('[data-line-img]');
        if (img) {
          const src = item.image || item.featured_image?.url || item.featured_image?.src || '';
          img.src = src; img.alt = item.product_title || item.title || '';
        }
        const setTxt = (sel, t) => {
          const el = node.querySelector(sel);
          if (el && t != null) el.textContent = t;
        };
        setTxt('[data-line-title]',   item.product_title || item.title || '');
        setTxt('[data-line-variant]', item.variant_title || '');
        setTxt('[data-line-qty]',     String(item.quantity || 1));
        setTxt('[data-line-price]',   formatMoney(item.line_price ?? (item.price * item.quantity)));

        // Selling plan / subscription label
        const planEl = node.querySelector('[data-line-selling-plan]');
        const planName = item.selling_plan_allocation?.selling_plan?.name;
        if (planEl) {
          if (planName) { planEl.textContent = `↻ ${planName}`; planEl.hidden = false; }
          else planEl.hidden = true;
        }

        // Bundle-line indicator (apps set _bundle_id on line item properties)
        const props = item.properties || {};
        if (props._bundle_id || props.bundle) node.classList.add('cart-drawer__line--bundle');

        return node;
      }

      function renderShipping(cart) {
        if (!shippingEl) return;
        const threshold = Number(App.data.cart_settings?.free_shipping_threshold_cents || 0);
        if (!threshold) { shippingEl.hidden = true; return; }
        shippingEl.hidden = false;
        const remaining = Math.max(0, threshold - (cart.items_subtotal_price || cart.total_price || 0));
        const pct = Math.min(100, Math.round(((cart.items_subtotal_price || cart.total_price || 0) / threshold) * 100));
        shipFillEl.style.width = pct + '%';
        if (remaining === 0) {
          shipTextEl.textContent = 'Je komt in aanmerking voor gratis verzending 🎉';
          shippingEl.classList.add('is-reached');
        } else {
          shipTextEl.textContent = `Voeg ${formatMoney(remaining)} toe voor gratis verzending`;
          shippingEl.classList.remove('is-reached');
        }
      }

      // ── Upsell rail ──────────────────────────────────────────────
      //   Source order: Shopify *complementary* recommendations first
      //   ("smart"), then merchant-curated cards fill the remaining
      //   slots up to the rail limit. Curated cards are server-rendered;
      //   smart cards are cloned from a <template> and prepended.
      //
      //   Degrades cleanly: no recs route (mock mode), toggle off, fetch
      //   failure, or zero recos → curated-only, exactly as before.

      // Normalize a recommendations.json product → card data (price in cents).
      // Keeps the full variant list so the card can offer swatches; the default
      // selection is the first available variant.
      function normalizeReco(p) {
        const productImage = p.featured_image || p.image || (p.images && p.images[0]) || '';
        const variants = (p.variants || []).map(vr => ({
          id:               String(vr.id || ''),
          title:            vr.title || '',
          price:            vr.price,
          compare_at_price: vr.compare_at_price,
          available:        !!vr.available,
          image:            vr.featured_image || productImage
        })).filter(vr => vr.id);
        const selected = variants.find(vr => vr.available) || variants[0];
        if (!selected) return null;
        return {
          product_id:       String(p.id || ''),
          variant_id:       selected.id,
          title:            p.title || '',
          image:            productImage,
          price:            selected.price,
          compare_at_price: selected.compare_at_price,
          variants:         variants
        };
      }

      // Fetch complementary recos for the cart's products. Anchors on up to
      // the first 3 distinct products to keep request count bounded.
      async function fetchComplementary(productIds, excludeVariants) {
        const limit  = railLimit();
        const anchors = productIds.slice(0, 3);
        const seenProducts = new Set();
        const out = [];
        const results = await Promise.all(anchors.map(async (pid) => {
          const url = `${recsUrl}.json?product_id=${encodeURIComponent(pid)}&intent=complementary&limit=${limit}`;
          try {
            const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
            if (!res.ok) return [];
            const data = await res.json();
            return Array.isArray(data.products) ? data.products : [];
          } catch (_) { return []; }
        }));
        results.flat().forEach(p => {
          const card = normalizeReco(p);
          if (!card || !card.variant_id) return;
          if (seenProducts.has(card.product_id)) return;     // de-dupe across anchors
          if (excludeVariants.has(card.variant_id)) return;  // already in cart
          seenProducts.add(card.product_id);
          out.push(card);
        });
        return out.slice(0, limit);
      }

      function clearSmartCards() {
        upsellRail.querySelectorAll('[data-upsell-source="smart"]').forEach(n => n.remove());
      }

      // Build swatch chips for a smart card's variants into its swatches box.
      // Hidden (no chips) for single-variant products.
      function buildSwatches(node, variants, selectedId) {
        const box = node.querySelector('[data-upsell-swatches]');
        if (!box) return;
        if (!variants || variants.length < 2) { box.hidden = true; return; }
        const frag = document.createDocumentFragment();
        variants.forEach(vr => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cd-upsell__swatch' + (vr.id === selectedId ? ' is-selected' : '');
          btn.setAttribute('data-upsell-swatch', '');
          btn.dataset.variantId = vr.id;
          btn.dataset.price     = String(vr.price || 0);
          btn.dataset.image     = vr.image || '';
          btn.dataset.title     = vr.title;
          btn.title             = vr.title;
          btn.setAttribute('aria-pressed', vr.id === selectedId ? 'true' : 'false');
          if (!vr.available) { btn.disabled = true; btn.setAttribute('aria-disabled', 'true'); }
          const label = document.createElement('span');
          label.className = 'cd-upsell__swatch-label';
          label.textContent = vr.title;
          btn.appendChild(label);
          frag.appendChild(btn);
        });
        box.appendChild(frag);
        box.hidden = false;
      }

      function injectSmartCards(recos) {
        if (!upsellCardTpl || !recos.length) return;
        const frag = document.createDocumentFragment();
        recos.forEach(p => {
          const node = upsellCardTpl.cloneNode(true);
          node.dataset.variantId = p.variant_id;
          node.dataset.productId = p.product_id;
          const img = node.querySelector('[data-upsell-img]');
          if (img) { img.src = p.image || ''; img.alt = p.title; }
          const nameEl = node.querySelector('[data-upsell-name]');
          if (nameEl) nameEl.textContent = p.title;
          const priceEl = node.querySelector('[data-upsell-price]');
          if (priceEl) {
            priceEl.textContent = formatMoney(p.price);
            if (p.compare_at_price && p.compare_at_price > p.price) {
              priceEl.appendChild(document.createTextNode(' '));
              const s = document.createElement('s');
              s.textContent = formatMoney(p.compare_at_price);
              priceEl.appendChild(s);
            }
          }
          const addBtn = node.querySelector('[data-cart-upsell-add]');
          if (addBtn) {
            addBtn.dataset.variantId = p.variant_id;
            addBtn.dataset.image     = p.image || '';
            addBtn.dataset.title     = p.title;
            addBtn.dataset.price     = String(p.price || 0);
            addBtn.setAttribute('aria-label', `Voeg ${p.title} toe aan winkelwagen`);
          }
          buildSwatches(node, p.variants, p.variant_id);
          frag.appendChild(node);
        });
        upsellRail.insertBefore(frag, upsellRail.firstChild);
      }

      // Fetch smart cards once per distinct cart-product set; guard re-entry.
      let smartFetchedKey = null;
      let smartInFlight   = false;
      async function ensureSmartCards(cart, inCartVariants) {
        if (!smartOn() || !upsellCardTpl) return;
        const productIds = [...new Set(
          (cart.items || []).map(i => String(i.product_id || '')).filter(Boolean)
        )];
        if (!productIds.length) { clearSmartCards(); smartFetchedKey = null; return; }
        const key = productIds.join(',');
        if (key === smartFetchedKey || smartInFlight) return;
        smartInFlight = true;
        try {
          const recos = await fetchComplementary(productIds, inCartVariants);
          clearSmartCards();
          injectSmartCards(recos);
          smartFetchedKey = key;
        } catch (err) {
          console.warn('[cart-drawer] smart upsell fetch failed', err);
          // curated cards remain as fallback
        } finally {
          smartInFlight = false;
        }
      }

      // Keep each stepper's decrement disabled at the floor.
      function refreshSteppers() {
        upsellRail.querySelectorAll('[data-cart-upsell-card]').forEach(card => {
          const n = parseInt(card.querySelector('[data-upsell-qty-n]')?.textContent, 10) || MIN_QTY;
          const dec = card.querySelector('[data-upsell-qty-dec]');
          if (dec) dec.disabled = n <= MIN_QTY;
        });
      }

      // Apply a variant choice to its card: re-point the card + add button at
      // the chosen variant, update price + thumbnail, and move the selected mark.
      function selectSwatch(card, swatch) {
        if (!card || !swatch || swatch.classList.contains('is-selected')) return;
        const vid   = swatch.dataset.variantId;
        const price = Number(swatch.dataset.price) || 0;
        const image = swatch.dataset.image || '';

        card.querySelectorAll('[data-upsell-swatch]').forEach(s => {
          const on = s === swatch;
          s.classList.toggle('is-selected', on);
          s.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        card.dataset.variantId = vid;
        const addBtn = card.querySelector('[data-cart-upsell-add]');
        if (addBtn) {
          addBtn.dataset.variantId = vid;
          addBtn.dataset.price     = String(price);
          if (image) addBtn.dataset.image = image;
        }

        // Price reflects the chosen variant (no compare-at on swatch switch).
        const priceEl = card.querySelector('[data-upsell-price], .cd-upsell__price');
        if (priceEl) priceEl.textContent = formatMoney(price);

        // Swap the thumbnail to the variant image when one is provided.
        const img = card.querySelector('.cd-upsell__img img');
        if (img && image) img.src = image;
      }

      async function renderUpsell(cart) {
        if (!upsellEl || !upsellRail) return;
        const inCartVariants = new Set(
          (cart.items || []).map(i => String(i.variant_id || i.id || ''))
        );

        // Pull smart recos (async, progressive — curated shows immediately).
        await ensureSmartCards(cart, inCartVariants);

        // Visibility + dedupe pass over the whole rail (smart first in DOM):
        //  - hide a card whose currently-selected variant is already in cart
        //  - hide the same product appearing twice (smart wins over curated)
        //  - cap visible cards at the rail limit
        const cards = upsellRail.querySelectorAll('[data-cart-upsell-card]');
        if (!cards.length) { upsellEl.hidden = true; return; }
        const limit = railLimit();
        const seenProducts = new Set();
        let visible = 0;
        cards.forEach(card => {
          const vid  = card.dataset.variantId;
          const pid  = card.dataset.productId;
          const dup  = inCartVariants.has(vid) || (pid && seenProducts.has(pid));
          const over = visible >= limit;
          const hide = dup || over;
          card.hidden = hide;
          if (!hide) { visible++; if (pid) seenProducts.add(pid); }
        });
        upsellEl.hidden = visible === 0;
        if (upsellCount) {
          upsellCount.textContent = visible + ' ' + (visible === 1 ? 'product' : 'producten');
        }
        refreshSteppers();
      }

      function renderDiscounts(cart) {
        if (!discountsEl) return;
        const apps = cart.cart_level_discount_applications || [];
        if (!apps.length) { discountsEl.hidden = true; discountsEl.innerHTML = ''; return; }
        discountsEl.innerHTML = apps.map(a => `
          <div class="cart-drawer__discount">
            <span>${a.title || 'Korting'}</span>
            <span>${formatMoney(a.total_allocated_amount || 0)}</span>
          </div>
        `).join('');
        discountsEl.hidden = false;
      }

      function render(cart) {
        if (!cart) return;
        const items = cart.items || [];

        // Header count
        if (countTextEl) countTextEl.textContent = `(${cart.item_count || 0})`;

        // Empty vs populated
        const empty = items.length === 0;
        if (emptyEl)  emptyEl.hidden  = !empty;
        if (footerEl) footerEl.hidden = empty;
        if (itemsEl)  itemsEl.hidden  = empty;

        // Subtotal
        if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price ?? cart.total_price ?? 0);

        // Reuse existing line nodes when keys match; only patch what changed.
        const existing = new Map(
          [...itemsEl.querySelectorAll('.cart-drawer__line')].map(n => [n.dataset.lineKey, n])
        );
        const seen = new Set();

        items.forEach(item => {
          const key = item.key || '';
          let node = existing.get(key);
          if (node) {
            // Patch in place (no rebuild)
            node.querySelector('[data-line-qty]').textContent   = String(item.quantity || 1);
            node.querySelector('[data-line-price]').textContent = formatMoney(item.line_price ?? (item.price * item.quantity));
            node.classList.remove('is-pending');
          } else {
            node = renderLine(item);
            if (node) itemsEl.appendChild(node);
          }
          seen.add(key);
        });
        // Remove lines that no longer exist
        existing.forEach((node, key) => { if (!seen.has(key)) node.remove(); });

        renderShipping(cart);
        renderDiscounts(cart);
        renderUpsell(cart);

        lastRenderedKey = items.map(i => `${i.key}:${i.quantity}`).join('|');
      }

      // ── Toggle bindings (every cart icon on every page) ──
      const toggles = document.querySelectorAll('.cart-btn, [data-cart-toggle]');
      toggles.forEach(btn => ctx.on(btn, 'click', (e) => { e.preventDefault(); setOpen(true); }));

      if (closeBtn) ctx.on(closeBtn, 'click', () => setOpen(false));
      if (overlay)  ctx.on(overlay,  'click', () => setOpen(false));
      ctx.on(document, 'keydown', (e) => { if (e.key === 'Escape' && isOpen) setOpen(false); });

      // ── "Verder winkelen" CTA (empty-cart state) ─────
      // If the user is already on a storefront page that's NOT the cart,
      // just close the drawer — no navigation needed. Otherwise let the
      // anchor's href (all-products collection) navigate normally.
      const continueBtn = drawer.querySelector('[data-cart-continue]');
      if (continueBtn) {
        ctx.on(continueBtn, 'click', (e) => {
          const path = window.location.pathname || '';
          const onCart = path === '/cart' || path.indexOf('/cart/') === 0;
          const onCheckout = path.indexOf('/checkout') === 0;
          if (!onCart && !onCheckout) {
            e.preventDefault();
            setOpen(false);
          }
        });
      }

      // ── Line-item interactions (event delegation) ────
      if (itemsEl) {
        ctx.on(itemsEl, 'click', async (e) => {
          const lineEl = e.target.closest('.cart-drawer__line');
          if (!lineEl) return;
          const key = lineEl.dataset.lineKey;
          const item = (App.cart.state?.items || []).find(i => i.key === key);
          if (!item) return;

          if (e.target.closest('[data-line-increment]')) {
            await App.cart.change({ key, quantity: item.quantity + 1 });
          } else if (e.target.closest('[data-line-decrement]')) {
            const q = Math.max(0, item.quantity - 1);
            if (q === 0) lineEl.classList.add('is-pending'); // optimistic fade
            await App.cart.change({ key, quantity: q });
          } else if (e.target.closest('[data-line-remove]')) {
            lineEl.classList.add('is-pending'); // optimistic fade
            await App.cart.change({ key, quantity: 0 });
          }
        });
      }

      // ── Upsell add: delegate clicks, go through App.cart.add.
      //    We DO NOT touch cart.js — just reuse the existing API.
      if (upsellRail) {
        ctx.on(upsellRail, 'click', async (e) => {
          // ── Quantity stepper (−/+) ───────────────────────────────
          const stepBtn = e.target.closest('[data-upsell-qty-inc], [data-upsell-qty-dec]');
          if (stepBtn) {
            const card = stepBtn.closest('[data-cart-upsell-card]');
            const nEl  = card?.querySelector('[data-upsell-qty-n]');
            if (!nEl) return;
            const inc = stepBtn.matches('[data-upsell-qty-inc]');
            let n = parseInt(nEl.textContent, 10) || MIN_QTY;
            n = inc ? Math.min(MAX_QTY, n + 1) : Math.max(MIN_QTY, n - 1);
            nEl.textContent = String(n);
            const dec = card.querySelector('[data-upsell-qty-dec]');
            if (dec) dec.disabled = n <= MIN_QTY;
            return;
          }

          // ── Variant swatch select ────────────────────────────────
          const swatch = e.target.closest('[data-upsell-swatch]');
          if (swatch) {
            if (!swatch.disabled) selectSwatch(swatch.closest('[data-cart-upsell-card]'), swatch);
            return;
          }

          // ── Add to cart (qty from this card's stepper) ───────────
          const btn = e.target.closest('[data-cart-upsell-add]');
          if (!btn || btn.classList.contains('is-loading')) return;
          const card = btn.closest('[data-cart-upsell-card]');
          const variantId = Number(btn.dataset.variantId);
          if (!variantId) return;
          const quantity = Math.max(
            MIN_QTY,
            parseInt(card?.querySelector('[data-upsell-qty-n]')?.textContent, 10) || MIN_QTY
          );

          btn.classList.add('is-loading');
          try {
            await App.cart.add({
              id:       variantId,
              quantity: quantity,
              image:    btn.dataset.image  || '',
              title:    btn.dataset.title  || '',
              price:    Number(btn.dataset.price) || 0
            });
            // Visual confirmation; renderUpsell on the next cart:updated
            // tick will hide the card (variant is now in the cart).
            if (card) card.classList.add('is-added');
            const def = btn.querySelector('.cd-upsell__add-label:not(.cd-upsell__add-label--added)');
            const add = btn.querySelector('.cd-upsell__add-label--added');
            if (def) def.hidden = true;
            if (add) add.hidden = false;
          } catch (err) {
            console.warn('[cart-drawer] upsell add failed', err);
          } finally {
            btn.classList.remove('is-loading');
          }
        });
      }

      // ── Checkout ──────────────────────────────────────
      if (checkoutBtn) {
        ctx.on(checkoutBtn, 'click', () => {
          const c = App.cart.state;
          if (!c?.items?.length) return;
          window.location.href = '/checkout';
        });
      }

      // ── Subscriptions: ONLY data path is cart:updated ──
      const off1 = App.bus.on('cart:updated',     render);
      const off2 = App.bus.on('cart:item-added',  () => setOpen(true));

      // ── Free-shipping threshold milestone events ─────
      let prevReached = null;
      const off3 = App.bus.on('cart:updated', (cart) => {
        const threshold = Number(App.data.cart_settings?.free_shipping_threshold_cents || 0);
        if (!threshold) return;
        const reached = (cart.items_subtotal_price || cart.total_price || 0) >= threshold;
        if (prevReached !== reached) {
          App.bus.emit(reached ? 'free-shipping:reached' : 'free-shipping:not-reached', { cart, threshold });
          prevReached = reached;
        }
      });

      // Initial render with current cart state (mock or live)
      if (App.cart?.state) render(App.cart.state);

      ctx.signal.addEventListener('abort', () => { off1(); off2(); off3(); });
    }
  });

  App.route('*', ['cart.drawer']);

})(window.App);
